import React, { useState, useEffect, useMemo } from 'react';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import PageHeader from './PageHeader';
import MobileHeader from './MobileHeader';
import MainTableV2 from './MainTableV2';
import TransactionDetailsModal from './TransactionsDetailsModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGasPump } from '@fortawesome/free-solid-svg-icons';
import { Bar, Line } from 'react-chartjs-2';
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import moment from 'moment-timezone';

ChartJS.register(BarElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TransactionTableUser = () => {
  const { theme } = useTheme();
  const { currentUser } = useAuth();
  const primaryColor = theme.primary;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState(moment().tz('America/Chicago').subtract(30, 'days').startOf('day').toDate());
  const [endDate, setEndDate] = useState(moment().tz('America/Chicago').endOf('day').toDate());
  const [totalAmount, setTotalAmount] = useState(0);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalDiscount, setTotalDiscount] = useState(0);
  const [averageDiscount, setAverageDiscount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [isFiltersOpen, setIsFiltersOpen] = useState(() => {
    const savedState = localStorage.getItem('filtersOpen');
    return savedState ? JSON.parse(savedState) : true;
  });

  const clearAlert = () => setAlertMessage(null);
  const apiUrl= process.env.REACT_APP_API_URL;

  const filterConfig = [
    { key: 'TransactionId', type: 'string' },
    { key: 'ArNumber', type: 'string' },
    { key: 'CardNumber', type: 'string' },
    { key: 'locationName', type: 'string' },
    { key: 'locationstate', type: 'string' },
    { key: 'locationCity', type: 'string' },
    { key: 'UserAmountSum', type: 'number' }
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'getTransactions',
          payload: {
            userId: currentUser?.userId,
            startDate: moment(startDate).tz('America/Chicago').startOf('day').utc().format(),
            endDate: moment(endDate).tz('America/Chicago').endOf('day').utc().format(),
          }
        })
      });

      if (response.ok) {
        const fetchedData = await response.json();
        const sortedData = fetchedData.sort((a, b) => new Date(b.TransactionDate) - new Date(a.TransactionDate));
        setData(sortedData);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []); // Removed dependencies to prevent automatic re-fetch on date change

  const handleViewDetails = async (TransactionId) => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/dataServer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'getTransactionDetails',
          payload: { userId: currentUser?.userId, TransactionId }
        })
      });
  
      if (response.ok) {
        const data = await response.json();
        setTransactionDetails(data);
        setIsModalOpen(true);
      } else {
        setAlertMessage(`Server Error: ${await response.text()}`);
      }
    } catch (error) {
      setAlertMessage(`Error fetching transaction details: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  

  const filteredData = useMemo(() => {
    let filtered = data;
    let sum = 0;
    let discountSum = 0;

    // Text search filter
    if (searchQuery !== '') {
      filtered = data.filter((row) => {
        return Object.values(row).some((s) =>
          s && s.toString().toLowerCase().includes(searchQuery.toLowerCase())
        );
      });
    }

    // Date filter
    if (startDate || endDate) {
      filtered = filtered.filter(row => {
        const rowDate = new Date(row.TransactionDate);
        const validStartDate = startDate && !isNaN(startDate.getTime());
        const validEndDate = endDate && !isNaN(endDate.getTime());
        return (!validStartDate || rowDate >= startDate) && (!validEndDate || rowDate <= endDate);
      });
    }

    filtered.forEach(transaction => {
      // Ensure UserAmountSum is a valid number before adding to sum
      const userAmount = parseFloat(transaction.UserAmountSum);
      if (!isNaN(userAmount)) {
        sum += userAmount;
      }
      const userDiscount = parseFloat(transaction.AvgUserDiscount);
      if (!isNaN(userDiscount)) {
        discountSum += userDiscount;
      }
    });

    setTotalAmount(sum);
    setTotalTransactions(filtered.length);
    setTotalDiscount(discountSum);
    setAverageDiscount(discountSum / filtered.length || 0);

    return filtered;
  }, [searchQuery, data, startDate, endDate]);

  const topLocationData = useMemo(() => {
    const locationCount = {};

    filteredData.forEach(transaction => {
      const location = transaction.locationName;
      if (!locationCount[location]) {
        locationCount[location] = 0;
      }
      locationCount[location] += 1;
    });

    const topLocations = Object.entries(locationCount).sort((a, b) => b[1] - a[1]).slice(0, 5);

    return {
      labels: topLocations.map(l => l[0]),
      datasets: [{
        label: 'Top Locations by Transactions',
        data: topLocations.map(l => l[1]),
        backgroundColor: primaryColor,
      }]
    };
  }, [filteredData, primaryColor]);

  // Chart data preparation - Daily spending last 7 days
  const last7DaysData = useMemo(() => {
    const last7Days = Array.from({ length: 7 }, (_, i) => {
      const date = moment().tz('America/Chicago').subtract(i, 'days').startOf('day');
      return { 
        date: date.format('YYYY-MM-DD'), 
        label: date.format('MMM DD'),
        amount: 0 
      };
    }).reverse();

    filteredData.forEach(transaction => {
      const transactionDate = transaction.TransactionDate.split('T')[0];
      const day = last7Days.find(d => d.date === transactionDate);
      if (day) {
        day.amount += transaction.UserAmountSum;
      }
    });

    return {
      labels: last7Days.map(d => d.label),
      datasets: [{
        label: 'Daily Spending',
        data: last7Days.map(d => d.amount),
        backgroundColor: `${primaryColor}80`, // Semi-transparent
        borderColor: primaryColor,
        borderWidth: 2,
      }]
    };
  }, [filteredData, primaryColor]);

  // Moving average for smoother transaction trends
  const averageAmountPerTransactionData = useMemo(() => {
    const days = 14; // Reduced to 2 weeks for better readability
    const dataPoints = Array.from({ length: days }, (_, i) => {
      const date = moment().tz('America/Chicago').subtract(i, 'days').startOf('day');
      return { 
        date: date.format('YYYY-MM-DD'),
        label: date.format('MMM DD'), 
        totalAmount: 0, 
        count: 0 
      };
    }).reverse();

    filteredData.forEach(transaction => {
      const transactionDate = transaction.TransactionDate.split('T')[0];
      const day = dataPoints.find(d => d.date === transactionDate);
      if (day) {
        day.totalAmount += transaction.UserAmountSum;
        day.count += 1;
      }
    });

    // Calculate 3-day moving average for smoother line
    const rawData = dataPoints.map(d => (d.count ? d.totalAmount / d.count : 0));
    const smoothedData = rawData.map((value, index) => {
      if (index < 2) return value;
      const window = rawData.slice(index - 2, index + 1);
      return window.reduce((sum, v) => sum + v, 0) / window.length;
    });

    return {
      labels: dataPoints.map(d => d.label),
      datasets: [{
        label: 'Average per Transaction (3-day moving avg)',
        data: smoothedData,
        backgroundColor: 'transparent',
        borderColor: primaryColor,
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: primaryColor,
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      }]
    };
  }, [filteredData, primaryColor]);

  // Smoothed price per gallon data
  const averagePricePerGallonData = useMemo(() => {
    const days = 14; // Reduced to 2 weeks for consistency
    const dataPoints = Array.from({ length: days }, (_, i) => {
      const date = moment().tz('America/Chicago').subtract(i, 'days').startOf('day');
      return { 
        date: date.format('YYYY-MM-DD'),
        label: date.format('MMM DD'), 
        totalPPU: 0, 
        count: 0 
      };
    }).reverse();

    filteredData.forEach(transaction => {
      const transactionDate = transaction.TransactionDate.split('T')[0];
      const day = dataPoints.find(d => d.date === transactionDate);
      if (day && transaction.AvgUserPPU) {
        day.totalPPU += transaction.AvgUserPPU;
        day.count += 1;
      }
    });

    // Calculate 3-day moving average for smoother line
    const rawData = dataPoints.map(d => (d.count ? d.totalPPU / d.count : 0));
    const smoothedData = rawData.map((value, index) => {
      if (index < 2) return value;
      const window = rawData.slice(index - 2, index + 1).filter(v => v > 0);
      return window.length ? window.reduce((sum, v) => sum + v, 0) / window.length : 0;
    });

    return {
      labels: dataPoints.map(d => d.label),
      datasets: [{
        label: 'Average Price per Gallon (3-day moving avg)',
        data: smoothedData,
        backgroundColor: 'transparent',
        borderColor: '#f59e0b', // Amber color for price
        borderWidth: 3,
        fill: false,
        tension: 0.4,
        pointBackgroundColor: '#f59e0b',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
      }]
    };
  }, [filteredData]);

  const columns = useMemo(() => [
    {
      name: "Date",
      selector: (row) => {
        return moment(row.TransactionDate).tz('America/Chicago').format('YYYY-MM-DD HH:mm:ss');
      },
      sortable: true,
      width: '160px'
    },
    { 
      name: 'Transaction ID', 
      selector: row => row.TransactionId,
      sortable: true,
      hide: 'sm',
    },
    { 
      name: 'Location', 
      selector: row => row.locationName,
      sortable: true,           
    },
    { 
      name: 'City', 
      selector: row => row.locationCity,
      sortable: true,      
    },
    { 
      name: 'State', 
      selector: row => row.locationstate,
      sortable: true,      
    },
    {
      name: 'Amount',
      selector: row => `$${parseFloat(row.UserAmountSum).toFixed(2)}`,
      sortable: true,
      width: '100px',
    },
    {
      name: 'View Details',
      cell: (row) => (
        <button
          className="btn-base btn-primary btn-sm"
          onClick={() => handleViewDetails(row.TransactionId)}
        >
          View
        </button>
      ),
      ignoreRowClick: false,       
    }
  ], [handleViewDetails]);

  const toggleFilters = () => {
    const newState = !isFiltersOpen;
    setIsFiltersOpen(newState);
    localStorage.setItem('filtersOpen', JSON.stringify(newState));
  };

  return (
    <>
      <MobileHeader theme={theme} />
      <div className='mobile-container'>
        <PageHeader
          heading="Fuel Transactions"
          context="View and search transaction details."
          icon={<FontAwesomeIcon icon={faGasPump} />}
        />

        <MainTableV2
          columns={columns}
          data={filteredData}
          primaryColor={primaryColor}
          loading={loading}
          alertMessage={alertMessage}
          clearAlert={clearAlert}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          filterConfig={filterConfig}
          dashboard={
            <div className="bg-white rounded-lg shadow-md p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <div 
                  className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-sm flex-shrink-0"
                  style={{ backgroundColor: `${primaryColor}15` }}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: primaryColor }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <div className="min-w-0">
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900 truncate">Transaction Analytics</h2>
                  <p className="text-xs sm:text-sm text-gray-600 truncate">Summary, trends, and filtering tools</p>
                </div>
              </div>
              <button 
                onClick={toggleFilters} 
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors duration-200 group"
              >
                <span className="text-sm font-medium">{isFiltersOpen ? 'Hide Analytics' : 'Show Analytics'}</span>
                <svg 
                  className={`w-4 h-4 transition-transform duration-200 ${isFiltersOpen ? 'rotate-180' : ''}`} 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
            {isFiltersOpen && (
              <div className="flex flex-col space-y-4">
                {/* Modern Summary Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3 sm:gap-4 mb-6">
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Amount</p>
                        <p className="text-lg sm:text-2xl font-bold text-green-600 truncate">${totalAmount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Transactions</p>
                        <p className="text-lg sm:text-2xl font-bold text-blue-600 truncate">{totalTransactions.toLocaleString()}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Average Discount</p>
                        <p className="text-lg sm:text-2xl font-bold text-purple-600 truncate">${averageDiscount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-100 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 0v1m-2 0V6a2 2 0 00-2 0v1m2 0V5.5a2 2 0 10-2 0v1m2 0h-2m0 0V5a2 2 0 012-2h0m0 0h2m-2 0h2m0 0a2 2 0 012 2v2.5" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Savings</p>
                        <p className="text-lg sm:text-2xl font-bold text-amber-600 truncate">${totalDiscount.toFixed(2)}</p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-br from-white to-gray-50 rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1">
                    <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                      <div 
                        className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: `${primaryColor}20` }}
                      >
                        <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: primaryColor }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Top Location</p>
                        <p className="text-sm sm:text-lg font-bold truncate" style={{ color: primaryColor }}>
                          {topLocationData.labels[0] || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Modern Charts Section */}
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                  <div className="bg-gradient-to-br from-white via-gray-50 to-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">Avg per Transaction</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">14-day smoothed trend</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <Line 
                        data={averageAmountPerTransactionData} 
                        options={{ 
                          responsive: true, 
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          elements: {
                            line: { tension: 0.4 },
                            point: { radius: 3, hoverRadius: 6 }
                          }
                        }} 
                        height={200}
                      />
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-white via-gray-50 to-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <div className="bg-gradient-to-r from-emerald-50 to-green-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">Daily Spending</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">Last 7 days breakdown</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <Bar 
                        data={last7DaysData} 
                        options={{ 
                          responsive: true, 
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          elements: { bar: { borderRadius: 4 } }
                        }} 
                        height={200}
                      />
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-br from-white via-gray-50 to-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-50 to-amber-50 px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100">
                      <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-orange-100 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 sm:w-4 sm:h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                          </svg>
                        </div>
                        <div className="min-w-0">
                          <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">Price per Gallon</h3>
                          <p className="text-xs sm:text-sm text-gray-600 truncate">14-day smoothed avg</p>
                        </div>
                      </div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <Line 
                        data={averagePricePerGallonData} 
                        options={{ 
                          responsive: true, 
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          elements: {
                            line: { tension: 0.4 },
                            point: { radius: 3, hoverRadius: 6 }
                          }
                        }} 
                        height={200}
                      />
                    </div>
                  </div>
                </div>

                {/* Enhanced Filters Section */}
                <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-gray-200 p-4 sm:p-6 mt-6">
                  <div className="flex items-center gap-2 sm:gap-3 mb-4">
                    <div className="w-6 h-6 sm:w-8 sm:h-8 bg-slate-200 rounded-full flex items-center justify-center">
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                      </svg>
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">Date Range Filters</h3>
                      <p className="text-xs sm:text-sm text-gray-600 truncate">Adjust the date range to update all charts and data</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Start Date</label>
                      <input
                        type="date"
                        value={moment(startDate).format('YYYY-MM-DD')}
                        onChange={(e) => setStartDate(moment(e.target.value).toDate())}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">End Date</label>
                      <input
                        type="date"
                        value={moment(endDate).format('YYYY-MM-DD')}
                        onChange={(e) => setEndDate(moment(e.target.value).toDate())}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">&nbsp;</label>
                      <button
                        className="btn-base btn-primary w-full h-[42px]"
                        onClick={fetchData}
                      >
                        Update Data
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>
          }
        />
        <TransactionDetailsModal
          isOpen={isModalOpen}
          details={transactionDetails}
          onClose={() => setIsModalOpen(false)}
        />
      </div>
    </>
  );
};

export default TransactionTableUser;


